import {
  emptyFileState,
  SOURCE_OF_FUNDS_DOC_TYPE_OPTIONS_BY_SOURCE,
  SOURCE_OF_FUNDS_OPTIONS,
} from "./constants";

export const normalizeKey = (value = "") => String(value).toLowerCase().replace(/[-\s]/g, "_");

export const getResponseBody = (response) => response?.data?.data || response?.data || response;

export const getRequirementId = (requirement, index) =>
  requirement?._id ||
  requirement?.id ||
  requirement?.requestId ||
  `${requirement?.key || requirement?.label || requirement?.type || "field"}-${index}`;

export const isApproved = (requirement = {}) =>
  normalizeKey(requirement.status || requirement.document?.status || requirement.requestStatus) ===
  "approved";

export const normalizeRejectedResponse = (response) => {
  const body = getResponseBody(response) || {};

  return {
    businessName: body.businessName || body.business?.name || "",
    status: body.status || body.business?.status || "",
    rejectedData: Array.isArray(body.rejectedData) ? body.rejectedData : [],
  };
};

export const normalizeRequirement = (requirement, index) => {
  const type = normalizeKey(requirement?.type || "text");
  const key = requirement?.key || normalizeKey(requirement?.label || `field_${index}`);

  return {
    ...requirement,
    id: getRequirementId(requirement, index),
    key,
    type,
    label: requirement?.label || key.replace(/_/g, " "),
  };
};

export const normalizeUploadResponse = (raw = {}, fallbackName = "") => {
  const url =
    (typeof raw === "string" && raw) ||
    raw?.url ||
    raw?.link ||
    raw?.url?.link ||
    raw?.data?.url ||
    raw?.data?.link ||
    raw?.data?.url?.link ||
    "";

  const key =
    raw?.key ||
    raw?.fileKey ||
    raw?.data?.key ||
    raw?.data?.fileKey ||
    raw?.name ||
    fallbackName ||
    "";

  return { url, key, name: fallbackName || key };
};

export const getOptionLabel = (options, value) =>
  options.find((option) => option.value === value)?.label || value;

const normalizeOptionValue = (value = "") => normalizeKey(String(value || ""));

const findOptionValue = (options = [], value = "") => {
  const normalizedValue = normalizeOptionValue(value);
  if (!normalizedValue) return "";

  return (
    options.find(
      (option) =>
        normalizeOptionValue(option.value) === normalizedValue ||
        normalizeOptionValue(option.label) === normalizedValue
    )?.value || ""
  );
};

const getSourceOfFundRequirementDocument = (requirement = {}) =>
  requirement.sourceOfFund ||
  requirement.source_of_fund ||
  requirement.sourceOfFunds ||
  requirement.source_of_funds ||
  requirement.document ||
  requirement.value ||
  {};

const getSourceOfFundInitialValue = (requirement = {}) => {
  const sourceDocument = getSourceOfFundRequirementDocument(requirement);
  const rawType = typeof sourceDocument === "string" ? "" : sourceDocument.type || "";
  const rawName = typeof sourceDocument === "string" ? "" : sourceDocument.name || "";
  const source =
    findOptionValue(
      SOURCE_OF_FUNDS_OPTIONS,
      sourceDocument.source ||
        sourceDocument.sourceOfFund ||
        sourceDocument.source_of_fund ||
        sourceDocument.name ||
        sourceDocument.type
    ) ||
    findOptionValue(SOURCE_OF_FUNDS_OPTIONS, sourceDocument.type);

  const docOptions = SOURCE_OF_FUNDS_DOC_TYPE_OPTIONS_BY_SOURCE[source] || [];
  const documentType =
    findOptionValue(
      docOptions,
      sourceDocument.documentType ||
        sourceDocument.document_type ||
        sourceDocument.type ||
        sourceDocument.name
    ) || "";

  const document = {
    url:
      (typeof sourceDocument === "string" && sourceDocument) ||
      sourceDocument.url ||
      sourceDocument.link ||
      sourceDocument.document?.url ||
      "",
    key: sourceDocument.key || sourceDocument.document?.key || "",
    name: sourceDocument.key || sourceDocument.name || sourceDocument.document?.key || "",
  };

  return {
    source,
    documentType,
    rawType,
    rawName,
    document: document.url || document.key ? document : null,
  };
};

export const getDateMax = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
};

export const formatAmount = (value = "") => {
  const cleaned = String(value).replace(/\D/g, "");
  if (!cleaned) return "";
  return Number(cleaned).toLocaleString("en-US");
};

export const getInitialValue = (requirement) => {
  if (requirement.type === "document" || requirement.type === "upload") {
    return {
      url: requirement.document?.url || "",
      key: requirement.document?.key || "",
      name: requirement.document?.key || "",
    };
  }

  if (requirement.type === "address") {
    const address = requirement.objectValue || requirement.address || {};
    return {
      houseNumber: address.houseNumber || "",
      street: address.street || "",
      city: address.city || "",
      state: address.state || "",
      zipCode: address.zipCode || address.zipcode || "",
      subdivision: address.subdivision || "",
    };
  }

  if (requirement.type === "web_presence") {
    return {
      hasWebsite: true,
      businessWebsite: requirement.webPresence?.businessWebsite || "",
      document: null,
    };
  }

  if (requirement.type === "source_of_fund" || requirement.type === "source_of_funds") {
    return getSourceOfFundInitialValue(requirement);
  }

  if (requirement.type === "monthly_transaction") {
    return {
      currency: "NGN",
      amount: "",
    };
  }

  if (
    requirement.key === "nature_of_business" ||
    requirement.type === "nature_of_business" ||
    normalizeKey(requirement.key) === "nature_of_business" ||
    normalizeKey(requirement.type) === "nature_of_business"
  ) {
    const val = requirement.objectValue || requirement.value || requirement.textValue;
    if (typeof val === "object" && val !== null) {
      return val.code || val.nature || val.value || "";
    }
    return String(val || "").trim();
  }

  if (Array.isArray(requirement.value)) return requirement.value.join(", ");
  return requirement.textValue || requirement.value || "";
};

export const getEmptyRequirementValue = (requirement) => {
  if (requirement.type === "document" || requirement.type === "upload") {
    return { url: "", key: "", name: "" };
  }

  if (requirement.type === "address") {
    return {
      houseNumber: "",
      street: "",
      city: "",
      state: "",
      zipCode: "",
      subdivision: "",
    };
  }

  if (requirement.type === "web_presence") {
    return { hasWebsite: true, businessWebsite: "", document: null };
  }

  if (requirement.type === "source_of_fund" || requirement.type === "source_of_funds") {
    return {
      source: "",
      documentType: "",
      rawType: "",
      rawName: "",
      document: null,
    };
  }

  if (requirement.type === "monthly_transaction") {
    return { currency: "NGN", amount: "" };
  }

  return "";
};

export const existingFileState = (value) => {
  if (!value?.url && !value?.key && !value?.name) return emptyFileState;
  return {
    file: { name: value.name || value.key || "Uploaded document", size: 0 },
    loading: false,
    uploaded: true,
    progress: 100,
  };
};

export const getFieldValue = (values, requirement) => values[requirement.id];

export const hasDocumentValue = (values, files, requirement) => {
  const value = getFieldValue(values, requirement);
  return Boolean(value?.url || files[requirement.id]?.file);
};

export const assignTargetIdentifier = (obj, rawTarget) => {
  if (!rawTarget || !obj) return;
  obj.targetId = rawTarget;
  delete obj.targetEmail;
  delete obj.target_email;
  delete obj.target_id;
  delete obj.targetID;
};

export const getRequirementTargetId = (requirement, isDirector) => {
  const list = isDirector
    ? requirement?.directors || requirement?.objectValue
    : requirement?.ubos || requirement?.objectValue;
  const arrayItemId = Array.isArray(list)
    ? list.find((item) => item?.targetId || item?.target_id || item?.targetID || item?.id)?.targetId ||
      list.find((item) => item?.targetId || item?.target_id || item?.targetID || item?.id)?.target_id ||
      list.find((item) => item?.targetId || item?.target_id || item?.targetID || item?.id)?.targetID ||
      list.find((item) => item?.targetId || item?.target_id || item?.targetID || item?.id)?.id ||
      list.find((item) => item?.targetEmail || item?.target_email || item?.email)?.targetEmail ||
      list.find((item) => item?.targetEmail || item?.target_email || item?.email)?.target_email ||
      list.find((item) => item?.targetEmail || item?.target_email || item?.email)?.email ||
      list[0]?.targetId || list[0]?.target_id || list[0]?.targetID || list[0]?.id || list[0]?.targetEmail || list[0]?.email
    : list?.targetId || list?.target_id || list?.targetID || list?.id || list?.targetEmail || list?.email;

  return (
    requirement?.targetId ||
    requirement?.target_id ||
    requirement?.targetID ||
    arrayItemId ||
    requirement?.targetEmail ||
    requirement?.target_email ||
    requirement?.email ||
    ""
  );
};

export const isRejectedDirectorRequirement = (req) => {
  const type = normalizeKey(req?.type || "");
  const subtype = normalizeKey(req?.subtype || req?.subType || req?.sub_type || "");
  const isDirectorType = type === "director" || subtype === "director" || subtype === "directors";
  return isDirectorType && !isApproved(req);
};

export const isRejectedUboRequirement = (req) => {
  const type = normalizeKey(req?.type || "");
  const subtype = normalizeKey(req?.subtype || req?.subType || req?.sub_type || "");
  const isUboType =
    type === "ubo" ||
    subtype === "ubo" ||
    subtype === "ubos" ||
    subtype === "control_person" ||
    subtype === "beneficial_owner";
  return isUboType && !isApproved(req);
};

export const getPrefillDataForType = (type, targetRequirement, rawRejectedData = [], business = {}) => {
  const isDirector = type === "director";
  const targetId =
    targetRequirement?.targetId ||
    targetRequirement?.target_id ||
    targetRequirement?.targetID ||
    targetRequirement?.targetEmail ||
    targetRequirement?.email ||
    "";
  const targetName = targetRequirement?.targetName || "";

  const rejectedUbosList =
    rawRejectedData.find((item) => Array.isArray(item?.ubos))?.ubos ||
    (Array.isArray(targetRequirement?.ubos) ? targetRequirement.ubos : []);
  const rejectedDirectorsList =
    rawRejectedData.find((item) => Array.isArray(item?.directors))?.directors ||
    (Array.isArray(targetRequirement?.directors) ? targetRequirement.directors : []);

  const businessUbosList = Array.isArray(business?.UBOs)
    ? business.UBOs
    : Array.isArray(business?.ubos)
      ? business.ubos
      : [];
  const businessDirectorsList = Array.isArray(business?.directors)
    ? business.directors
    : [];

  const candidateLists = isDirector
    ? [rejectedDirectorsList, businessDirectorsList, rejectedUbosList, businessUbosList]
    : [rejectedUbosList, businessUbosList, rejectedDirectorsList, businessDirectorsList];

  for (const list of candidateLists) {
    if (!Array.isArray(list) || list.length === 0) continue;

    if (targetId) {
      const match = list.find((item) => {
        const itemId = item?.targetId || item?._id || item?.id || item?.email || item?.targetEmail;
        return itemId && String(itemId).toLowerCase() === String(targetId).toLowerCase();
      });
      if (match) return match;
    }

    if (targetName) {
      const match = list.find((item) => {
        const fullName = `${item?.firstName || ""} ${item?.lastName || ""}`.trim();
        return fullName && fullName.toLowerCase() === targetName.toLowerCase();
      });
      if (match) return match;
    }

    if (isDirector) {
      const directorUbo = list.find((item) => item?.isDirector === true);
      if (directorUbo) return directorUbo;
    }

    if (list.length === 1 && list[0]) {
      return list[0];
    }
  }

  const matchingFields = rawRejectedData.filter(
    (item) =>
      (targetId && (item?.targetId === targetId || item?.email === targetId)) ||
      (targetName && item?.targetName === targetName) ||
      (isDirector && isRejectedDirectorRequirement(item)) ||
      (!isDirector && isRejectedUboRequirement(item))
  );

  if (matchingFields.length > 0) {
    const synthetic = {};
    if (targetId) {
      synthetic.targetId = targetId;
      if (targetId.includes("@")) synthetic.email = targetId;
    }
    if (targetName) {
      const parts = targetName.trim().split(" ");
      synthetic.firstName = parts[0] || "";
      synthetic.lastName = parts.slice(1).join(" ") || "";
    }

    matchingFields.forEach((field) => {
      const key = normalizeKey(field.key || field.label || "");
      if (key === "name") {
        const val = field.textValue || field.value || targetName || "";
        const parts = String(val).trim().split(" ");
        if (parts[0]) synthetic.firstName = parts[0];
        if (parts.length > 1) synthetic.lastName = parts.slice(1).join(" ");
      } else if (key === "first_name" || key === "firstname") {
        synthetic.firstName = field.textValue || field.value || "";
      } else if (key === "last_name" || key === "lastname") {
        synthetic.lastName = field.textValue || field.value || "";
      } else if (key === "email" || key === "email_address") {
        synthetic.email = field.textValue || field.value || "";
      } else if (key === "phone_number" || key === "phone") {
        synthetic.phoneNumber = field.textValue || field.value || "";
      } else if (key === "date_of_birth" || key === "dob") {
        synthetic.dateOfBirth = field.textValue || field.value || "";
      } else if (key === "gender") {
        synthetic.gender = field.textValue || field.value || "";
      } else if (key === "title") {
        synthetic.title = field.textValue || field.value || "";
      } else if (key === "percentage_ownership" || key === "ownership_share") {
        synthetic.percentageOwnership = field.textValue || field.value || "";
      } else if (key === "bvn" || key === "bvn_number") {
        synthetic.bvn = field.textValue || field.value || "";
      } else if (key === "id_number" || key === "identification_number") {
        synthetic.idNumber = field.textValue || field.value || "";
      }
    });

    if (Object.keys(synthetic).length > 0) {
      return synthetic;
    }
  }

  return null;
};

export const hasValue = (values, files, requirement, moreData = {}) => {
  const requirementType = normalizeKey(requirement?.type || "");

  if (requirementType === "director") {
    return Boolean(
      moreData?.director &&
      typeof moreData.director === "object" &&
      Object.keys(moreData.director).length > 0
    );
  }

  if (requirementType === "ubo") {
    return Boolean(
      moreData?.ubo &&
      typeof moreData.ubo === "object" &&
      Object.keys(moreData.ubo).length > 0
    );
  }

  const value = getFieldValue(values, requirement);

  switch (requirement.type) {
    case "document":
    case "upload":
      return hasDocumentValue(values, files, requirement);
    case "address":
      return Boolean(value?.street && value?.city && value?.state && value?.zipCode);
    case "web_presence":
      return value?.hasWebsite
        ? Boolean(value?.businessWebsite)
        : Boolean(value?.document?.url || files[`${requirement.id}-web`]?.file);
    case "source_of_fund":
    case "source_of_funds":
      if (value?.document?.url || value?.document?.key) return true;
      return Boolean(
        value?.source &&
          value?.documentType &&
          files[`${requirement.id}-source`]?.file
      );
    case "monthly_transaction":
      return Boolean(value?.currency && value?.amount);
    case "country":
      return typeof value === "object" ? Boolean(value?.name) : Boolean(String(value || "").trim());
    default:
      if (requirement.key?.includes("country")) {
        return typeof value === "object" ? Boolean(value?.name) : Boolean(String(value || "").trim());
      }
      return Boolean(String(value || "").trim());
  }
};

export const validateFile = (file) => {
  if (!file) return "Please upload all required documents.";
  if (file.size > 5 * 1024 * 1024) return "File size exceeds the limit of 5MB.";
  return "";
};
